import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Card, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { getJSON, patchJSON } from '@/lib/api'
import { displayDateTime } from '@/lib/dates'
import { OrganizationDashboardSettingDialogs } from './OrganizationDashboardSettingDialogs'
import type {
  OrganizationDashboardSettingChanges,
  OrganizationDashboardSettingResponse,
} from './OrganizationDashboardSettingPage.types'

const path = '/api/v1/organization_dashboard_setting'
const key = ['organization-dashboard-setting']
export function OrganizationDashboardSettingPage() {
  const client = useQueryClient()
  const [next, setNext] = useState<boolean | null>(null)
  const [ticked, setTicked] = useState(false)
  const query = useQuery({
    queryKey: key,
    queryFn: () => getJSON<OrganizationDashboardSettingResponse>(path),
    retry: false,
  })
  function close() {
    setNext(null)
    setTicked(false)
    mutation.reset()
  }
  const mutation = useMutation({
    mutationFn: (changes: OrganizationDashboardSettingChanges) =>
      patchJSON<OrganizationDashboardSettingResponse>(path, {
        organization_dashboard_setting: changes,
      }),
    onSuccess: (data) => {
      client.setQueryData(key, data)
      close()
    },
  })
  if (query.isLoading)
    return (
      <p role="status" className="text-body-sm text-muted-foreground">
        Loading organization dashboard setting...
      </p>
    )
  if (query.isError) return <Alert>{query.error.message}</Alert>
  const setting = query.data?.organization_dashboard_setting
  if (!setting) return <Alert>Organization dashboard setting unavailable.</Alert>
  const change = setting.last_change
  return (
    <div className="mx-auto w-full max-w-[1200px] space-y-6 px-6 py-6">
      <h1 className="text-heading-sm font-semibold text-foreground">Settings</h1>
      <Card className="max-w-2xl">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="grid gap-1">
              <Label htmlFor="org-dashboard-switch" className="text-body font-medium">
                Organization dashboard
              </Label>
              <p className="text-body-sm text-muted-foreground">
                Roster managers and billing contacts see their organization&apos;s activity in the
                member portal.
              </p>
              {change && (
                <p className="text-caption text-muted-foreground">
                  Turned {change.enabled ? 'on' : 'off'} by {change.actor_name} ·{' '}
                  {displayDateTime(change.changed_at)}
                </p>
              )}
              {!setting.can_manage && (
                <p className="text-caption text-muted-foreground">
                  Only an Administrator can change this.
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span className="text-body-sm text-muted-foreground">
                {setting.enabled ? 'On' : 'Off'}
              </span>
              <Switch
                id="org-dashboard-switch"
                checked={setting.enabled}
                disabled={!setting.can_manage || mutation.isPending}
                onCheckedChange={setNext}
              />
            </div>
          </div>
        </CardContent>
      </Card>
      <OrganizationDashboardSettingDialogs
        next={next}
        ticked={ticked}
        pending={mutation.isPending}
        error={mutation.error}
        onTick={setTicked}
        onClose={close}
        onConfirm={() => {
          if (next !== null)
            mutation.mutate({
              enabled: next,
              ...(next ? { privacy_notice_confirmed: ticked } : {}),
            })
        }}
      />
    </div>
  )
}
